import {
  AlignCenterOutlined,
  CalendarOutlined,
  DashOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  FontSizeOutlined,
  HolderOutlined,
  NumberOutlined,
  PictureOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Card, Flex, Tag, Tooltip, Typography } from "../../../../../shared/frontend/ui";
import React from "react";
import { borderRadius, cardDefaults, spacing, typography } from "../app/design-system";
import type { LocalizedText } from "../app/i18n/i18n";
import { useI18n } from "../app/i18n/useI18n";
import { setActivePaletteDragType } from "./documentBlockCanvas";

export type PaletteBlockItem = {
  type: string;
  label: LocalizedText;
  disabled?: boolean;
};

type Props = {
  items: PaletteBlockItem[];
  onAddBlock?: (type: string) => void;
  surface?: "letter" | "email";
  /**
   * Höhe des Blattfensters daneben. Die Leiste endet mit ihm, damit sie die Seite nicht über den
   * Bildschirm hinaus wachsen lässt; sie wird vom Editor gemessen und nicht hier gerechnet.
   */
  maxHeight?: number | null;
};

const REPEATABLE_TYPES = new Set(["text", "divider", "spacer"]);

const BLOCK_ICONS: Record<string, React.ReactNode> = {
  logo: <PictureOutlined />,
  sender: <UserOutlined />,
  address: <EnvironmentOutlined />,
  date: <CalendarOutlined />,
  subject: <FileTextOutlined />,
  salutation: <UserOutlined />,
  introduction: <FontSizeOutlined />,
  intro: <FontSizeOutlined />,
  notice: <FileTextOutlined />,
  hint: <FileTextOutlined />,
  closing: <AlignCenterOutlined />,
  conclusion: <FontSizeOutlined />,
  footer: <AlignCenterOutlined />,
  pageNumber: <NumberOutlined />,
  text: <FontSizeOutlined />,
  divider: <DashOutlined />,
  spacer: <HolderOutlined />,
};

export function DocumentBlockPalette({
  items,
  onAddBlock,
  surface: _surface = "letter",
  maxHeight,
}: Props) {
  const { text } = useI18n();

  const standardBlocks = items.filter((item) => !REPEATABLE_TYPES.has(item.type));
  const customBlocks = items.filter((item) => REPEATABLE_TYPES.has(item.type));

  const renderBlockCard = (item: PaletteBlockItem) => {
    const isRepeatable = REPEATABLE_TYPES.has(item.type);
    const icon = BLOCK_ICONS[item.type] ?? <FileTextOutlined />;

    const content = (
      <div
        draggable={!item.disabled}
        onDragStart={(event) => {
          if (item.disabled) {
            event.preventDefault();
            return;
          }
          setActivePaletteDragType(item.type);
          event.dataTransfer.setData("application/x-mq-block-type", item.type);
          event.dataTransfer.effectAllowed = "copy";
        }}
        onDragEnd={() => {
          setActivePaletteDragType(null);
        }}
        onClick={() => {
          if (!item.disabled) return onAddBlock?.(item.type);
        }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: spacing.sm,
          padding: `${spacing.xs}px ${spacing.sm}px`,
          borderRadius: borderRadius.md,
          backgroundColor: item.disabled ? "rgba(0, 0, 0, 0.02)" : "rgba(0, 0, 0, 0.03)",
          border: "1px solid rgba(0, 0, 0, 0.08)",
          cursor: item.disabled ? "not-allowed" : "grab",
          opacity: item.disabled ? 0.5 : 1,
          transition: "all 0.15s ease",
          userSelect: "none",
        }}
        onMouseEnter={(e) => {
          if (!item.disabled) {
            e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.06)";
            e.currentTarget.style.borderColor = "rgba(0, 0, 0, 0.18)";
          }
        }}
        onMouseLeave={(e) => {
          if (!item.disabled) {
            e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.03)";
            e.currentTarget.style.borderColor = "rgba(0, 0, 0, 0.08)";
          }
        }}
      >
        <span style={{ fontSize: typography.fontSize.base, color: item.disabled ? "#999" : "#555", display: "flex", alignItems: "center" }}>
          {icon}
        </span>
        <Typography.Text
          ellipsis
          style={{
            flex: 1,
            fontSize: typography.fontSize.compact,
            fontWeight: 500,
            color: item.disabled ? "#999" : "inherit",
          }}
        >
          {text(item.label)}
        </Typography.Text>
        {isRepeatable ? (
          <Tag style={{ margin: 0, paddingInline: spacing.xs, fontSize: typography.fontSize.xxs, lineHeight: `${typography.fontSize.md}px` }} color="default">
            +
          </Tag>
        ) : null}
      </div>
    );

    if (item.disabled) {
      return (
        <Tooltip key={item.type} title={text("Block already added")}>
          {content}
        </Tooltip>
      );
    }

    return (
      <Tooltip key={item.type} title={text("Drag into document or click to add")}>
        {content}
      </Tooltip>
    );
  };

  return (
    <Card
      size="small"
      style={{
        width: 220,
        flexShrink: 0,
        height: "fit-content",
        maxHeight: maxHeight ?? undefined,
        overflowY: "auto",
        ...cardDefaults.listCardBodyStyleCompact,
      }}
      styles={{ body: { padding: spacing.sm } }}
    >
      <Flex vertical gap={spacing.md}>
        <div>
          <Typography.Text strong style={{ fontSize: typography.fontSize.compact, display: "block", marginBottom: spacing.xs, color: "#666", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            {text("Document elements")}
          </Typography.Text>
          <Flex vertical gap={spacing.xs}>
            {standardBlocks.map(renderBlockCard)}
          </Flex>
        </div>

        {customBlocks.length > 0 ? (
          <div>
            <Typography.Text strong style={{ fontSize: typography.fontSize.compact, display: "block", marginBottom: spacing.xs, color: "#666", textTransform: "uppercase", letterSpacing: "0.04em" }}>
              {text("Custom elements")}
            </Typography.Text>
            <Flex vertical gap={spacing.xs}>
              {customBlocks.map(renderBlockCard)}
            </Flex>
          </div>
        ) : null}
      </Flex>
    </Card>
  );
}
